import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import {
  INSTALL_BANNER_STORAGE_KEY,
  type ClientPlatform,
  type InstallEnvironment,
  detectPlatform,
  shouldOfferInstall,
} from './install';

/**
 * Événement Chrome / Edge : le navigateur est prêt à afficher son dialogue
 * d'installation. Il n'existe pas dans les types DOM standard, et iOS ne
 * l'émet jamais — d'où l'interface locale.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Décide si la bannière d'installation doit apparaître, et déclenche le
 * dialogue natif Android quand Chrome le propose.
 *
 * Un refus (la croix) ne vit que le temps de la visite : `sessionStorage`
 * disparaît quand l'onglet est vraiment jeté. `localStorage` la ferait taire
 * pour toujours, y compris après avoir quitté Chrome — trop silencieux pour
 * une invitation qu'on veut répéter.
 */
@Injectable({ providedIn: 'root' })
export class InstallPromptService {
  private readonly destroyRef = inject(DestroyRef);
  private nativeEvent: BeforeInstallPromptEvent | undefined;

  readonly platform = signal<ClientPlatform>('other');
  readonly offer = signal(false);
  readonly canPrompt = signal(false);

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }
    this.platform.set(detectPlatform(readBrowserEnv()));
    this.refresh();
    this.listen();
    registerServiceWorker();
  }

  dismiss(): void {
    try {
      sessionStorage.setItem(INSTALL_BANNER_STORAGE_KEY, '1');
      localStorage.removeItem(INSTALL_BANNER_STORAGE_KEY);
    } catch {
      // Quota ou navigation privée : on masque au moins pour cette visite.
    }
    this.offer.set(false);
  }

  async promptInstall(): Promise<void> {
    const event = this.nativeEvent;
    if (!event) {
      return;
    }
    await event.prompt();
    const choice = await event.userChoice;
    this.nativeEvent = undefined;
    this.canPrompt.set(false);
    if (choice.outcome === 'accepted') {
      this.offer.set(false);
    }
  }

  private refresh(): void {
    const env = readBrowserEnv();
    this.platform.set(detectPlatform(env));
    this.offer.set(shouldOfferInstall(env, readDismissed()));
  }

  private listen(): void {
    const onPrompt = (event: Event) => {
      event.preventDefault();
      this.nativeEvent = event as BeforeInstallPromptEvent;
      this.canPrompt.set(true);
      this.refresh();
    };
    const onInstalled = () => {
      this.nativeEvent = undefined;
      this.canPrompt.set(false);
      this.offer.set(false);
    };
    const onDisplayMode = () => this.refresh();

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    const media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: standalone)')
        : undefined;
    media?.addEventListener('change', onDisplayMode);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      media?.removeEventListener('change', onDisplayMode);
    });
  }
}

function readDismissed(): boolean {
  try {
    // Ancienne version : un refus éternel en localStorage. On l'oublie pour
    // que la bannière puisse revenir après un redémarrage de Chrome.
    localStorage.removeItem(INSTALL_BANNER_STORAGE_KEY);
    return sessionStorage.getItem(INSTALL_BANNER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function media(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

export function readBrowserEnv(): InstallEnvironment {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    iosStandalone: nav.standalone === true,
    displayModeStandalone: media('(display-mode: standalone)'),
    coarsePointer: media('(pointer: coarse)'),
    narrowViewport: media('(max-width: 48rem)'),
  };
}

/**
 * Un service worker avec un gestionnaire `fetch` est exigé par Chrome pour
 * proposer « Installer l'application ». Il ne met rien en cache : l'offline
 * n'est pas l'objectif, seule l'installabilité l'est.
 */
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  const local =
    location.hostname === 'localhost' ||
    location.hostname === '127.0.0.1' ||
    location.hostname === '[::1]';
  if (local) {
    // Un SW enregistré pendant `ng serve` intercepte le rechargement à chaud.
    void navigator.serviceWorker.getRegistrations().then((regs) => {
      for (const registration of regs) {
        void registration.unregister();
      }
    });
    return;
  }
  if (!window.isSecureContext) {
    return;
  }
  void navigator.serviceWorker.register('/sw.js');
}
